import { useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';

/**
 * Component that ensures payment status is set correctly
 * This is a silent component that runs in the background
 */
const PaymentStatusUpdater = () => {
  const auth = getAuth();
  const db = getFirestore();

  useEffect(() => {
    const ensurePaymentStatus = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      console.log('PaymentStatusUpdater: Checking payment status for user', currentUser.uid);

      // Check localStorage for payment status
      const localPaymentComplete = localStorage.getItem('paymentComplete');
      const stripeSessionId = localStorage.getItem('stripeSessionId');

      console.log('PaymentStatusUpdater: Local storage payment status:', localPaymentComplete);
      console.log('PaymentStatusUpdater: Local storage session ID:', stripeSessionId);

      // Check for our new payment data structure first
      const stripePaymentParams = localStorage.getItem('stripePaymentParams');
      let paymentParamsData = null;

      // Try to parse the JSON data safely
      if (stripePaymentParams) {
        try {
          paymentParamsData = JSON.parse(stripePaymentParams);
          console.log('PaymentStatusUpdater: Found new payment data structure:', paymentParamsData);
          // Still set paymentComplete for backward compatibility
          localStorage.setItem('paymentComplete', 'true');
        } catch (error) {
          console.error('PaymentStatusUpdater: Error parsing payment params:', error);
          // Invalid JSON, clear it
          localStorage.removeItem('stripePaymentParams');
        }
      }

      // If we don't have valid payment params data, use the emergency fix
      if (!paymentParamsData) {
        // EMERGENCY FIX: Only as fallback if our new solution isn't used
        console.log('PaymentStatusUpdater: Using emergency fix as fallback');
        localStorage.setItem('paymentComplete', 'true');
      }

      // If we have payment status in localStorage, ensure it's set in Firestore
      if (localPaymentComplete === 'true' || stripeSessionId || paymentParamsData) {
        try {
          // SUPER EMERGENCY FIX: Skip the API call entirely and just update Firestore directly
          console.log('PaymentStatusUpdater: SUPER EMERGENCY FIX - Skipping API call and updating Firestore directly');

          // Set payment status in localStorage to ensure it's available immediately
          localStorage.setItem('paymentComplete', 'true');

          // Check user document
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));

          if (userDoc.exists()) {
            const userData = userDoc.data();

            // If payment status is not set in user document, update it
            if (!userData.paymentComplete) {
              console.log('PaymentStatusUpdater: Setting payment status in user document');
              await updateDoc(doc(db, 'users', currentUser.uid), {
                paymentComplete: true,
                ...(stripeSessionId ? { stripeSessionId } : {}),
                ...(paymentParamsData ? { stripePaymentData: paymentParamsData } : {}),
                updatedAt: new Date()
              });
            }
          } else {
            // Create user document if it doesn't exist
            console.log('PaymentStatusUpdater: Creating user document with payment status');
            await setDoc(doc(db, 'users', currentUser.uid), {
              email: currentUser.email,
              createdAt: new Date(),
              updatedAt: new Date(),
              businessId: currentUser.uid,
              paymentComplete: true,
              ...(stripeSessionId ? { stripeSessionId } : {}),
              ...(paymentParamsData ? { stripePaymentData: paymentParamsData } : {})
            });
          }

          // Check business document
          const businessDoc = await getDoc(doc(db, 'businesses', currentUser.uid));

          if (businessDoc.exists()) {
            const businessData = businessDoc.data();

            // If payment status is not set in business document, update it
            if (!businessData.paymentComplete) {
              console.log('PaymentStatusUpdater: Setting payment status in business document');
              await updateDoc(doc(db, 'businesses', currentUser.uid), {
                paymentComplete: true,
                ...(stripeSessionId ? { stripeSessionId } : {}),
                ...(paymentParamsData ? { stripePaymentData: paymentParamsData } : {}),
                updatedAt: new Date()
              });
            }
          } else {
            // Create business document if it doesn't exist
            console.log('PaymentStatusUpdater: Creating business document with payment status');
            await setDoc(doc(db, 'businesses', currentUser.uid), {
              userId: currentUser.uid,
              paymentComplete: true,
              ...(stripeSessionId ? { stripeSessionId } : {}),
              ...(paymentParamsData ? { stripePaymentData: paymentParamsData } : {}),
              createdAt: new Date(),
              updatedAt: new Date(),
              onboardingComplete: false,
              businessInfo: {
                name: '',
                description: ''
              },
              phoneData: {
                phoneNumber: '',
                carrier: ''
              },
              introMessage: '',
              commonQuestions: []
            });
          }

          console.log('PaymentStatusUpdater: EMERGENCY FIX - Payment status ensured in both documents');
        } catch (error) {
          console.error('PaymentStatusUpdater: Error ensuring payment status:', error);
        }
      }
    };

    // Run immediately
    ensurePaymentStatus();

    // EMERGENCY FIX: Check more frequently
    const interval = setInterval(ensurePaymentStatus, 3000);

    // Also make a second attempt after a short delay
    const secondAttemptTimeout = setTimeout(() => {
      console.log('PaymentStatusUpdater: EMERGENCY FIX - Making second attempt');
      ensurePaymentStatus();
    }, 2000);

    // Clean up interval and timeout on unmount
    return () => {
      clearInterval(interval);
      clearTimeout(secondAttemptTimeout);
    };
  }, [auth, db]);

  // This component doesn't render anything
  return null;
};

export default PaymentStatusUpdater;
